import type { Company, CompanyOfficer } from '@dash-lite/sdk';
import { Banknote, Building2, Calculator, Gavel, MapPin, Scale, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, Card, CardBody, CardHeader, DataList, DataRow } from '@/components/ui/primitives';
import { formatBoolean, formatCountry, formatDate, orDash } from '@/lib/format';

/**
 * Fiche d'information complète d'une entreprise.
 *
 * Ce composant illustre la promesse centrale de l'API : **un seul format de
 * réponse, quel que soit le pays du site**. Il est écrit une fois et rend
 * correctement une entreprise française, belge, portugaise ou suisse.
 *
 * La réponse est structurée en trois blocs :
 *
 * | Bloc       | Contenu                                            | Varie selon le pays ?          |
 * | ---------- | -------------------------------------------------- | ------------------------------ |
 * | noyau      | identité, adresse, forme juridique, dirigeants      | non                            |
 * | `registry` | ce que le registre national expose                 | les clés sont communes, les valeurs manquantes valent `null` |
 * | `derived`  | valeurs calculées par la plateforme                 | selon les règles du pays       |
 *
 * **Les clés ne disparaissent jamais** : un pays qui ne fournit pas une
 * information renvoie `null`. Le code n'a donc pas à tester l'existence des
 * champs, seulement leur valeur — c'est exactement ce que fait `orDash()`.
 */
export function CompanySheet({ company }: { company: Company }) {
    return (
        <div className="space-y-6">
            <IdentityCard company={company} />
            <RegistryCard company={company} />
            <OfficersCard officers={company.officers} />
            <DerivedCard company={company} />
            <ProviderFootnote company={company} />
        </div>
    );
}

/** Le noyau : ce que tous les registres fournissent. */
function IdentityCard({ company }: { company: Company }) {
    const { address } = company;

    const formattedAddress = [address.line1, [address.postal_code, address.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

    return (
        <Card>
            <CardHeader
                title="Identité"
                description="Le noyau de la fiche — identique pour tous les pays."
                icon={<Building2 className="size-4" />}
            />
            <CardBody>
                <DataList>
                    <DataRow label="Dénomination" value={company.name} />
                    <DataRow
                        label="Numéro d'immatriculation"
                        value={
                            <span className="font-mono">
                                {orDash(company.registry.registration_number_formatted ?? company.registration_number)}
                            </span>
                        }
                    />
                    <DataRow label="Forme juridique" value={orDash(company.legal_form)} />
                    <DataRow label="Statut" value={<CompanyStatusBadge status={company.status} />} />
                    <DataRow label="Date d'immatriculation" value={formatDate(company.incorporation_date)} />
                    <DataRow label="Numéro de TVA" value={<span className="font-mono">{orDash(company.vat_number)}</span>} />
                    <DataRow
                        label="Adresse"
                        value={
                            <span className="inline-flex items-start gap-1.5">
                                <MapPin className="text-ink-muted mt-0.5 size-3.5 shrink-0" aria-hidden />
                                <span>
                                    {formattedAddress || '—'}
                                    <span className="text-ink-muted block text-xs font-normal">
                                        {formatCountry(address.country)}
                                    </span>
                                </span>
                            </span>
                        }
                    />
                </DataList>
            </CardBody>
        </Card>
    );
}

/**
 * Le bloc `registry` : ce que le registre national expose, sous des clés
 * communes à tous les pays. La France est la plus complète ; les autres pays
 * renvoient la même structure avec davantage de `null`.
 */
function RegistryCard({ company }: { company: Company }) {
    const { registry } = company;

    return (
        <Card>
            <CardHeader
                title="Registre national"
                description="Les clés sont communes à tous les pays ; une valeur absente vaut null."
                icon={<Scale className="size-4" />}
            />

            <CardBody className="grid gap-x-10 gap-y-6 md:grid-cols-2">
                <section>
                    <SubTitle icon={<Scale className="size-3.5" />}>Immatriculation</SubTitle>
                    <DataList>
                        <DataRow label="Identifiant au registre" value={orDash(registry.registration_id)} />
                        <DataRow label="Statut au registre" value={orDash(registry.registration_status)} />
                        <DataRow label="Date d'immatriculation" value={formatDate(registry.registration_date)} />
                        <DataRow
                            label="Première immatriculation"
                            value={formatDate(registry.first_registration_date)}
                        />
                        <DataRow label="Numéro de gestion" value={orDash(registry.management_number)} />
                        <DataRow label="Durée statutaire" value={registry.duration_years ? `${registry.duration_years} ans` : '—'} />
                        <DataRow label="Date de radiation" value={formatDate(registry.striking_off_date)} />
                        <DataRow label="Date de cessation" value={formatDate(registry.cessation_date)} />
                    </DataList>
                </section>

                <section>
                    <SubTitle icon={<Banknote className="size-3.5" />}>Capital et activité</SubTitle>
                    <DataList>
                        <DataRow
                            label="Capital social"
                            value={
                                <span>
                                    {orDash(registry.capital.formatted)}
                                    {registry.capital.variable && (
                                        <Badge tone="neutral" className="ml-2">
                                            variable
                                        </Badge>
                                    )}
                                </span>
                            }
                        />
                        <DataRow label="Code d'activité" value={orDash(registry.activity.code)} />
                        <DataRow label="Activité" value={orDash(registry.activity.label)} />
                        <DataRow label="Domaine" value={orDash(registry.activity.domain)} />
                        <DataRow label="Nom commercial" value={orDash(registry.trade_name)} />
                        <DataRow label="Sigle" value={orDash(registry.acronym)} />
                        <DataRow label="Clôture de l'exercice" value={orDash(registry.fiscal_year_end)} />
                        <DataRow label="Prochaine clôture" value={formatDate(registry.next_fiscal_year_end)} />
                    </DataList>
                </section>

                <section>
                    <SubTitle icon={<Users className="size-3.5" />}>Effectifs et établissements</SubTitle>
                    <DataList>
                        <DataRow label="Effectif" value={orDash(registry.workforce.count)} />
                        <DataRow label="Tranche d'effectif" value={orDash(registry.workforce.range)} />
                        <DataRow label="Année de référence" value={orDash(registry.workforce.year)} />
                        <DataRow label="Employeur" value={formatBoolean(registry.workforce.is_employer)} />
                        <DataRow label="Établissements" value={orDash(registry.establishments_count)} />
                        <DataRow label="Établissements ouverts" value={orDash(registry.open_establishments_count)} />
                        <DataRow
                            label="Siège"
                            value={
                                [
                                    registry.headquarters.line1,
                                    registry.headquarters.line2,
                                    [registry.headquarters.postal_code, registry.headquarters.city]
                                        .filter(Boolean)
                                        .join(' '),
                                ]
                                    .filter(Boolean)
                                    .join(', ') || '—'
                            }
                        />
                    </DataList>
                </section>

                <section>
                    <SubTitle icon={<Gavel className="size-3.5" />}>Juridiction et mentions</SubTitle>
                    <DataList>
                        <DataRow label="Greffe / tribunal" value={orDash(registry.court.name)} />
                        <DataRow label="Code du greffe" value={orDash(registry.court.code)} />
                        <DataRow
                            label="Procédures collectives"
                            value={
                                registry.collective_proceedings.ongoing ? (
                                    <Badge tone="critical">Procédure en cours</Badge>
                                ) : registry.collective_proceedings.exists ? (
                                    <Badge tone="caution">Procédure clôturée</Badge>
                                ) : (
                                    <Badge tone="positive">Aucune</Badge>
                                )
                            }
                        />
                        <DataRow label="Personne morale" value={formatBoolean(registry.is_legal_entity)} />
                        <DataRow label="Société à mission" value={formatBoolean(registry.flags.mission_led)} />
                        <DataRow label="Économie sociale et solidaire" value={formatBoolean(registry.flags.social_economy)} />
                        <DataRow label="Associé unique" value={formatBoolean(registry.flags.sole_shareholder)} />
                        <DataRow label="Dernière mise à jour" value={formatDate(registry.last_updated_at)} />
                    </DataList>
                </section>
            </CardBody>
        </Card>
    );
}

/**
 * Les dirigeants. Les champs optionnels (`appointed_at`, `nationality`…) ne
 * sont fournis que par les registres qui les exposent : on les affiche
 * seulement quand ils sont là, plutôt que d'aligner des tirets.
 */
function OfficersCard({ officers }: { officers: CompanyOfficer[] }) {
    return (
        <Card>
            <CardHeader
                title="Dirigeants"
                description={`${officers.length} personne${officers.length > 1 ? 's' : ''} enregistrée${officers.length > 1 ? 's' : ''} au registre.`}
                icon={<Users className="size-4" />}
            />
            <CardBody>
                {officers.length === 0 ? (
                    <p className="text-ink-muted text-sm">Le registre ne publie aucun dirigeant pour cette entreprise.</p>
                ) : (
                    <ul className="divide-line divide-y">
                        {officers.map((officer, index) => (
                            <li key={`${officer.name}-${index}`} className="flex flex-wrap gap-x-6 gap-y-1 py-3 first:pt-0 last:pb-0">
                                <div className="min-w-48 flex-1">
                                    <p className="text-ink text-sm font-medium">{officer.name}</p>
                                    <p className="text-ink-muted text-xs">{orDash(officer.role)}</p>
                                </div>

                                <dl className="text-ink-muted flex flex-wrap gap-x-6 gap-y-1 text-xs">
                                    {officer.appointed_at && (
                                        <div>
                                            <dt className="inline">Nommé le </dt>
                                            <dd className="text-ink-soft inline">{formatDate(officer.appointed_at)}</dd>
                                        </div>
                                    )}
                                    {officer.nationality && (
                                        <div>
                                            <dt className="inline">Nationalité </dt>
                                            <dd className="text-ink-soft inline">{officer.nationality}</dd>
                                        </div>
                                    )}
                                    {officer.birth_date && (
                                        <div>
                                            <dt className="inline">Né(e) le </dt>
                                            <dd className="text-ink-soft inline">{formatDate(officer.birth_date)}</dd>
                                        </div>
                                    )}
                                    {officer.birth_place && (
                                        <div>
                                            <dt className="inline">à </dt>
                                            <dd className="text-ink-soft inline">{officer.birth_place}</dd>
                                        </div>
                                    )}
                                    {officer.is_legal_entity && <Badge tone="neutral">Personne morale</Badge>}
                                </dl>
                            </li>
                        ))}
                    </ul>
                )}
            </CardBody>
        </Card>
    );
}

/**
 * Le bloc `derived` : ce que la plateforme calcule.
 *
 * Ces valeurs ne figurent pas telles quelles dans les registres. Les calculer
 * côté plateforme évite que chaque site réinvente la règle — et diverge.
 * **Ce qui ne peut pas être calculé vaut `null` : rien n'est inventé.**
 */
function DerivedCard({ company }: { company: Company }) {
    const { derived } = company;

    const hasAnyValue =
        derived.european_identifier || derived.management_number || derived.court || derived.entity_end_date;

    return (
        <Card>
            <CardHeader
                title="Calculé par la plateforme"
                description="Valeurs dérivées, produites au même endroit pour tous les sites."
                icon={<Calculator className="size-4" />}
            />
            <CardBody>
                {!hasAnyValue ? (
                    <p className="text-ink-muted text-sm">
                        Aucune valeur dérivée pour ce pays : les règles de calcul ne sont pas configurées, et rien
                        n'est inventé pour combler le vide.
                    </p>
                ) : (
                    <DataList>
                        <DataRow
                            label="Identifiant européen (EUID)"
                            value={<span className="font-mono">{orDash(derived.european_identifier)}</span>}
                        />
                        <DataRow label="Numéro de gestion" value={orDash(derived.management_number)} />
                        <DataRow
                            label="Juridiction compétente"
                            value={
                                derived.court ? (
                                    <span>
                                        {derived.court.label}
                                        <span className="text-ink-muted block text-xs font-normal">
                                            {[
                                                derived.court.address.line1,
                                                [derived.court.address.postal_code, derived.court.address.city]
                                                    .filter(Boolean)
                                                    .join(' '),
                                            ]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </span>
                                    </span>
                                ) : (
                                    '—'
                                )
                            }
                        />
                        <DataRow
                            label="Fin de la personne morale"
                            value={formatDate(derived.entity_end_date)}
                        />
                    </DataList>
                )}
            </CardBody>
        </Card>
    );
}

/**
 * Origine réelle de la donnée : `fixtures` en environnement de test,
 * `pappers` ou `openapi` en production selon le pays.
 */
function ProviderFootnote({ company }: { company: Company }) {
    return (
        <p className="text-ink-muted text-xs">
            Source de la donnée&nbsp;: <span className="font-mono">{company.provider_data.provider}</span>. Le bloc{' '}
            <span className="font-mono">provider_data.raw</span> contient la réponse brute du fournisseur ; son contenu
            varie d'un pays à l'autre et ne fait pas partie du contrat d'API — mieux vaut ne rien construire dessus.
        </p>
    );
}

/** Le statut normalisé : `active`, `ceased` ou `unknown`, quel que soit le pays. */
export function CompanyStatusBadge({ status }: { status: Company['status'] }) {
    switch (status) {
        case 'active':
            return <Badge tone="positive">En activité</Badge>;
        case 'ceased':
            return <Badge tone="critical">Cessée</Badge>;
        default:
            return <Badge tone="neutral">Statut inconnu</Badge>;
    }
}

function SubTitle({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
    return (
        <h4 className="text-ink-muted mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
            {icon}
            {children}
        </h4>
    );
}
